import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { concatMap, filter, map, of, Subscription } from 'rxjs';

import { environment } from '../../../../../../environments/environment';
import { EventsApi } from '../../../../../api/events/events-api';
import { LoginApi } from '../../../../../api/login/login.api';

import { ConfigService } from '../../../../../Core/Config/services/config.service';

import { UserStateModel } from '../../../../../Core/Users/models/user-state.model';
import { UserModel } from '../../../../../Core/Users/models/user.model';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';

import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { AccountSummaryModel } from '@Core/Users/models/account-summary.model';
import { HttpVerbsEnum } from '../../../../../api/shared/enums/http-verbs.enum';
import { WebsocketAwareNeptuneService } from '../../../../../Bootstrap/services/websocket-aware-neptune.service';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { AccountManagementService } from '../../../services/account-management.service';
import { UserProfileComponent } from '../user-profile.component';


enum UmtTabEnum {
    changeLog = 0,
    activityLog = 1,
}

@Component({
    selector: 'agpr-user-profile-page',
    templateUrl: './user-profile-page.component.html',
    styleUrls: ['./user-profile-page.component.scss'],
    standalone: false
})
export class UserProfilePageComponent implements OnDestroy {

    subscriptions: Subscription[] = [];

    user: UserModel;
    userState: UserStateModel;
    primaryAccount: AccountSummaryModel;

    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;

    isReadOnly: boolean = false;
    isAlreadyGhostedIn: boolean = false;
    hasGhostInAccess: boolean = false;

    selectedTabIndex: UmtTabEnum = UmtTabEnum.changeLog;

    loading: boolean = false;

    @ViewChild('classicForm') classicForm: ElementRef;
    @ViewChild('userProfile') userProfile: UserProfileComponent;

    public constructor(
        public accountManagementService: AccountManagementService,
        public currentUserStateService: CurrentUserStateService,
        public loginApi: LoginApi,
        public eventsApi: EventsApi,
        public router: Router,
        public configService: ConfigService,
        private websocketAwareNeptuneService: WebsocketAwareNeptuneService
    ) {
        this.subscriptions.push(
            this.accountManagementService.getSelectedUserPrimaryAccount().subscribe(primaryAccount => this.primaryAccount = primaryAccount),
            this.accountManagementService.getSelectedUser().pipe(filter(x => !!x)).subscribe(user => {
                this.user = user;
            }),
            this.currentUserStateService.getState().pipe(filter(x => !!x)).subscribe(userState => {
                this.userState = userState;
                this.isAlreadyGhostedIn = !!this.userState?.impersonator;
            }),
            this.currentUserStateService.hasGhostInAccess().subscribe(hasGhostInAccess => {
                this.hasGhostInAccess = hasGhostInAccess;
            }),
            this.accountManagementService.getReadOnly().subscribe(result => {
                this.isReadOnly = result;
            })
        );
    }

    ghostIn(accountId?: number) {
        // if current account is netpune only.. then do call, else just submit
        this.currentUserStateService.startGhostIn();
        this.loginApi.ghostIn({ userId: this.user.id }).pipe(
            map(done => {
                if (this.user.neptuneOnly || !this.primaryAccount) {
                    this.currentUserStateService.clearState();
                    this.currentUserStateService.loadUserState();
                    this.currentUserStateService.ignoreWebsockets = true;
                    this.websocketAwareNeptuneService.sendWebsocketEventInstant('/login/ghostin', HttpVerbsEnum.POST);
                } else {
                    this.currentUserStateService.ignoreWebsockets = true;
                    this.websocketAwareNeptuneService.sendWebsocketEventInstant('/login/ghostin', HttpVerbsEnum.POST);
                    this.classicForm.nativeElement.submit();
                }
            }),
            concatMap(done => {
                if (accountId && accountId !== this.primaryAccount.id) {
                    return this.currentUserStateService.switchAccount(accountId);
                }
                return of(null);
            }),
            map(done => {
                this.websocketAwareNeptuneService.disconnectFromWebsocket();
                this.websocketAwareNeptuneService.connectToWebsocket();
            })
        ).subscribe({
            next: () => {
                if (this.user.neptuneOnly) {
                    this.router.navigate(['Dashboard']);
                    // HACK: refresh the page after redirect, we do this because things don't handle refreshing data properly on loadUserState,
                    // and localizations don't update either
                    requestAnimationFrame(() => {
                        window.location.reload();
                    });
                }
            },
            error: () => {}
        });

    }

    tabChange(tabIndex: UmtTabEnum) {
        this.selectedTabIndex = tabIndex;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(x => x.unsubscribe());
    }
}
